import { createFileRoute, Link } from "@tanstack/react-router";
import { Bullets, LegalPage, Note, Section, Value } from "@/components/legal-page";
import { LEGAL } from "@/lib/legal";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Xamseen — Contact & privacy requests" },
      {
        name: "description",
        content:
          "How to reach Xamseen: privacy and data requests, moderation appeals, safety concerns, accessibility barriers and legal notices.",
      },
      { property: "og:title", content: "Xamseen — Contact & privacy requests" },
      { property: "og:description", content: "Reach a human at Xamseen, and know where each kind of request goes." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://xamseen.com/contact" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://xamseen.com/contact" }],
  }),
  component: ContactPage,
});

const TOC = [
  { id: "self-serve", label: "Things you can do yourself" },
  { id: "who", label: "Who to contact" },
  { id: "privacy-requests", label: "Making a privacy request" },
  { id: "entity", label: "Legal entity details" },
];

function ContactPage() {
  return (
    <LegalPage
      title="Contact & privacy requests"
      intro="A real person reads these. Below is where each kind of request should go, and what to include so we can act on it without a round trip."
      toc={TOC}
    >
      <Note tone="warn">
        Xamseen is not an emergency service. If there is an immediate risk to life, health or safety, contact your local
        emergency number first.
      </Note>

      <Section id="self-serve" title="Things you can do yourself, immediately">
        <p>These do not need a support request — they take effect straight away:</p>
        <Bullets
          items={[
            <>
              <strong>Get a copy of your data</strong> — Settings → Privacy &amp; your data → Download my data.
            </>,
            <>
              <strong>Delete your account and content</strong> — same panel → Delete my account.
            </>,
            <>
              <strong>Change location precision or profile visibility</strong> — same panel.
            </>,
            <>
              <strong>Change what we email you about</strong> — Settings → Notifications, or the unsubscribe link in any
              notification email.
            </>,
            <>
              <strong>Correct your information</strong> — edit your profile.
            </>,
          ]}
        />
        <Link to="/settings" className="inline-block text-sm font-medium text-plum hover:underline">
          Open Settings
        </Link>
      </Section>

      <Section id="who" title="Who to contact">
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-semibold">Your request</th>
                <th className="px-4 py-3 font-semibold">Where it goes</th>
              </tr>
            </thead>
            <tbody className="align-top">
              <tr className="border-t border-border">
                <td className="px-4 py-3">
                  <strong>Privacy and data protection</strong>
                  <div className="text-muted-foreground">
                    Access, correction, deletion, portability, objection, restriction, withdrawing consent.
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Value value={LEGAL.privacyContactEmail} label="privacy contact email address" />
                </td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-3">
                  <strong>Moderation appeals</strong>
                  <div className="text-muted-foreground">Content removed or account restricted and you think we erred.</div>
                </td>
                <td className="px-4 py-3">
                  <Value value={LEGAL.supportEmail} label="general support and legal contact address" />
                </td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-3">
                  <strong>Safety and urgent harm</strong>
                  <div className="text-muted-foreground">
                    Threats, doxxing, content involving minors, coordinated abuse.
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Value value={LEGAL.supportEmail} label="general support and legal contact address" />
                </td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-3">
                  <strong>Accessibility barriers</strong>
                  <div className="text-muted-foreground">Something in Xamseen you cannot use.</div>
                </td>
                <td className="px-4 py-3">
                  <Value value={LEGAL.supportEmail} label="general support and accessibility contact address" />
                </td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-3">
                  <strong>Legal notices, IP complaints, law enforcement</strong>
                </td>
                <td className="px-4 py-3">
                  <Value value={LEGAL.supportEmail} label="general support and legal contact address" />, postal address
                  below
                </td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-3">
                  <strong>Security vulnerability reports</strong>
                  <div className="text-muted-foreground">
                    Please report privately and give us reasonable time to fix before disclosure.
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Value value={LEGAL.supportEmail} label="security contact address" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-muted-foreground">
          Service email reaches you from {LEGAL.emailSendingDomain}. We will never ask for your password by email.
        </p>
      </Section>

      <Section id="privacy-requests" title="Making a privacy request">
        <p>To handle a request quickly, include:</p>
        <Bullets
          items={[
            <>The email address or username on your Xamseen account.</>,
            <>What you want us to do — access, correct, delete, export, restrict, or object.</>,
            <>Any specific content or data the request concerns.</>,
          ]}
        />
        <p>
          We may need to confirm you control the account before acting on a request, to make sure we do not disclose or
          delete someone else's data. We do not charge for handling a request, and we respond within the timeframe required
          by the law that applies to you.
        </p>
        <p>
          If you are unhappy with how we handled your request, you can complain to your local data protection or privacy
          authority.
        </p>
      </Section>

      <Section id="entity" title="Legal entity details">
        <p>
          Xamseen is operated by <Value value={LEGAL.legalEntity} label="registered legal entity name" />.
        </p>
        <p>
          Registered / postal address: <Value value={LEGAL.postalAddress} label="registered or postal address" />
        </p>
        <p>
          Registration number: <Value value={LEGAL.registrationNumber} label="business registration number, if applicable" />
        </p>
        <p>
          Data protection officer or representative:{" "}
          <Value value={LEGAL.dataProtectionOfficer} label="DPO or representative, or a statement that none is appointed" />
        </p>
      </Section>
    </LegalPage>
  );
}
